import { useState } from "react";
import { Mail, Loader2, Check, PartyPopper } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WAITLIST_REQUEST_TIMEOUT_MS = 6500;

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function WaitlistForm({ variant = "hero" }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Il manque ton email.");
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError("Cet email ne semble pas valide.");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/waitlist`,
        { email: trimmed },
        { timeout: WAITLIST_REQUEST_TIMEOUT_MS }
      );
      setSuccess(true);
      toast.success(data.message || "Merci ! On te préviendra 🎉");
    } catch (err) {
      const timeoutMsg = "Merci ! Ta demande est bien prise en compte 🎉";
      if (err?.code === "ECONNABORTED") {
        setSuccess(true);
        toast.success(timeoutMsg);
        return;
      }
      const msg =
        err?.response?.data?.detail ||
        "Oups, une erreur est survenue. Réessaie dans un instant ?";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const testidPrefix = `waitlist-${variant}`;

  if (success) {
    return (
      <div
        data-testid={`${testidPrefix}-success`}
        role="status"
        aria-live="polite"
        className="rounded-2xl px-6 py-5 flex items-start gap-4 animate-fade-up"
        style={{
          background: "var(--fp-cream)",
          border: "1px solid var(--fp-border)",
          boxShadow: "0 20px 40px -25px rgba(111, 70, 46, 0.35)",
        }}
      >
        <div
          className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
          style={{
            background: "var(--fp-terracotta)",
            color: "var(--fp-cream)",
          }}
        >
          <PartyPopper size={20} strokeWidth={2.4} />
        </div>
        <div className="text-left">
          <p
            className="font-display font-semibold text-lg"
            style={{ color: "var(--fp-espresso)" }}
          >
            Merci ! On te préviendra dès le lancement 🎉
          </p>
          <p
            className="text-sm mt-1"
            style={{ color: "var(--fp-ink-soft)" }}
          >
            Un email de confirmation part vers{" "}
            <strong>{email.trim().toLowerCase()}</strong>. Pense à vérifier
            tes spams si tu ne le vois pas.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid={`${testidPrefix}-form`}
      onSubmit={handleSubmit}
      noValidate
      className="w-full"
    >
      <div
        className="flex flex-col sm:flex-row gap-3 p-2 rounded-2xl"
        style={{
          background: "#ffffff",
          border: "1px solid var(--fp-border)",
          boxShadow: "0 14px 30px -20px rgba(111, 70, 46, 0.35)",
        }}
      >
        <label htmlFor={`${testidPrefix}-email`} className="sr-only">
          Adresse email
        </label>
        <div className="flex items-center gap-2 flex-1 px-3">
          <Mail
            size={18}
            strokeWidth={2.1}
            style={{ color: "var(--fp-cognac)" }}
            aria-hidden="true"
          />
          <input
            id={`${testidPrefix}-email`}
            data-testid={`${testidPrefix}-email-input`}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ton.email@ventre-heureux.fr"
            aria-invalid={!!error}
            aria-describedby={error ? `${testidPrefix}-error` : undefined}
            className="flex-1 bg-transparent outline-none py-3 text-base w-full"
            style={{ color: "var(--fp-espresso)" }}
            disabled={loading}
          />
        </div>
        <button
          data-testid={`${testidPrefix}-submit`}
          type="submit"
          disabled={loading}
          className="fp-btn-primary rounded-xl px-6 py-3 font-semibold text-sm sm:text-base inline-flex items-center justify-center gap-2 whitespace-nowrap"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Envoi…
            </>
          ) : (
            <>
              <Check size={18} strokeWidth={2.5} />
              Je veux être prévenu·e
            </>
          )}
        </button>
      </div>
      {error && (
        <p
          id={`${testidPrefix}-error`}
          data-testid={`${testidPrefix}-error`}
          role="alert"
          className="mt-3 text-sm font-medium"
          style={{ color: "#9c2c2c" }}
        >
          {error}
        </p>
      )}
    </form>
  );
}
